/**
 * Agent tuning copilot (Agent console → Tuning tab).
 *
 * A chat box for admins: describe how the texting agent should behave
 * differently ("stop opening with 'Hi there!'", "the brief's stipend is
 * wrong", "slow the nudges down") and the copilot answers with PROPOSALS —
 * diff cards carrying full replacement text for a prompt/template, a study
 * brief, or a settings patch. Nothing ships from the chat itself:
 *   - prompt/brief proposals: "Apply draft" writes the draft, "Publish" is a
 *     separate explicit click through the EXISTING publish routes (so the
 *     V1–V6 template wall and validateAgentBrief still gate).
 *   - settings proposals: the single "Apply settings" click is the approval
 *     (numeric knobs only — the backend clamps; kill switch/mode unreachable).
 *
 * Admin-only (the tuner routes 403 otherwise — handled with an empty state).
 * Chat history is session-local React state; the backend is stateless.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, SendHorizontal, Loader2, Check, CheckCheck, AlertTriangle,
  FileText, MessageSquareText, SlidersHorizontal, ExternalLink, ShieldAlert,
} from 'lucide-react';
import api from '../../../services/api';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../components/filters/styles';
import MarkdownText from '../../components/MarkdownText';

// ---------------------------------------------------------------------------
// Types (mirror crmAgentTuner.js)
// ---------------------------------------------------------------------------

interface ProposalCheck { ok: boolean; failures: { id: string; reason: string }[] }

interface TunerProposal {
  kind: 'prompt' | 'brief' | 'settings';
  key?: string;
  new_template?: string;
  study_id?: string;
  new_text?: string;
  patch?: Record<string, any>;
  rationale: string | null;
  current?: any;
  checks?: ProposalCheck | null;
}

interface ChatMsg {
  role: 'user' | 'assistant';
  content: string;
  proposals?: { id: string; proposal: TunerProposal }[];
  notes?: string[];
  isError?: boolean;
}

interface TargetStudy { id: string; title: string; brief: { published: boolean; draft: boolean } }
interface TargetPrompt { key: string; name: string; customized: boolean; version: number }

type CardState =
  | { state: 'idle' }
  | { state: 'applying' }
  | { state: 'applied'; artifactId?: string }
  | { state: 'publishing'; artifactId?: string }
  | { state: 'published'; version?: number }
  | { state: 'error'; message: string; artifactId?: string };

// ---------------------------------------------------------------------------
// Diff rendering
// ---------------------------------------------------------------------------

type DiffLine = { type: 'same' | 'del' | 'add' | 'gap'; text: string };

/** Simple LCS line diff with long-unchanged-run collapsing. Falls back to
 *  del-all/add-all when either side is huge (keeps the DP bounded). */
function lineDiff(oldText: string, newText: string): DiffLine[] {
  const a = String(oldText ?? '').split('\n');
  const b = String(newText ?? '').split('\n');
  if (a.length > 400 || b.length > 400) {
    return [...a.map((t) => ({ type: 'del' as const, text: t })), ...b.map((t) => ({ type: 'add' as const, text: t }))];
  }
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const raw: DiffLine[] = [];
  let i = 0; let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) { raw.push({ type: 'same', text: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { raw.push({ type: 'del', text: a[i] }); i++; }
    else { raw.push({ type: 'add', text: b[j] }); j++; }
  }
  while (i < m) { raw.push({ type: 'del', text: a[i] }); i++; }
  while (j < n) { raw.push({ type: 'add', text: b[j] }); j++; }

  // Collapse unchanged runs longer than 7 lines to 2 lines of context each side.
  const out: DiffLine[] = [];
  let run: DiffLine[] = [];
  const flushRun = (isTail: boolean) => {
    if (run.length > 7) {
      out.push(...run.slice(0, 2));
      out.push({ type: 'gap', text: `… ${run.length - 4} unchanged lines …` });
      if (!isTail) out.push(...run.slice(-2));
    } else {
      out.push(...run);
    }
    run = [];
  };
  for (const line of raw) {
    if (line.type === 'same') run.push(line);
    else { flushRun(false); out.push(line); }
  }
  flushRun(true);
  return out;
}

function DiffBlock({ oldText, newText }: { oldText: string | null; newText: string }) {
  const lines = useMemo(
    () => (oldText == null || oldText === ''
      ? String(newText).split('\n').map((t) => ({ type: 'add' as const, text: t }))
      : lineDiff(oldText, newText)),
    [oldText, newText],
  );
  return (
    <div className="max-h-64 overflow-auto rounded-lg border border-gray-200 bg-gray-50 font-mono text-xs leading-5 dark:border-gray-700 dark:bg-gray-900/40">
      {lines.map((l, idx) => (
        <div
          key={idx}
          className={
            l.type === 'add' ? 'whitespace-pre-wrap bg-green-50 px-2 text-green-800 dark:bg-green-900/20 dark:text-green-300'
              : l.type === 'del' ? 'whitespace-pre-wrap bg-red-50 px-2 text-red-700 line-through decoration-red-300 dark:bg-red-900/20 dark:text-red-300 dark:decoration-red-700'
                : l.type === 'gap' ? 'px-2 py-0.5 text-center text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500'
                  : 'whitespace-pre-wrap px-2 text-gray-600 dark:text-gray-400'
          }
        >
          {l.type === 'gap' ? l.text : (l.text || ' ')}
        </div>
      ))}
    </div>
  );
}

/** Flatten a settings patch/current pair into "path: old → new" rows. */
function settingsRows(patch: Record<string, any>, current: Record<string, any> | undefined): { path: string; from: any; to: any }[] {
  const rows: { path: string; from: any; to: any }[] = [];
  for (const [k, v] of Object.entries(patch || {})) {
    if (v !== null && typeof v === 'object') {
      for (const [k2, v2] of Object.entries(v)) rows.push({ path: `${k}.${k2}`, from: current?.[k]?.[k2], to: v2 });
    } else {
      rows.push({ path: k, from: current?.[k], to: v });
    }
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Proposal card
// ---------------------------------------------------------------------------

const KIND_META: Record<TunerProposal['kind'], { label: string; icon: typeof FileText }> = {
  prompt: { label: 'Prompt edit', icon: MessageSquareText },
  brief: { label: 'Study brief edit', icon: FileText },
  settings: { label: 'Settings change', icon: SlidersHorizontal },
};

function ProposalCard({
  proposal, cardState, promptNames, studyTitle, onApply, onPublish,
}: {
  proposal: TunerProposal;
  cardState: CardState;
  promptNames: Map<string, string>;
  studyTitle: (id: string | undefined) => string;
  onApply: () => void;
  onPublish: () => void;
}) {
  const meta = KIND_META[proposal.kind];
  const Icon = meta.icon;
  const target = proposal.kind === 'prompt'
    ? (promptNames.get(proposal.key || '') || proposal.key)
    : proposal.kind === 'brief' ? studyTitle(proposal.study_id) : 'Agent settings';
  const checksFailed = proposal.checks && !proposal.checks.ok;
  const busy = cardState.state === 'applying' || cardState.state === 'publishing';
  const detailLink = proposal.kind === 'prompt'
    ? `/crm/settings/prompts/${proposal.key}`
    : proposal.kind === 'brief' && proposal.study_id ? `/crm/settings/studies/${proposal.study_id}` : null;

  return (
    <div className="mt-2 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/20 dark:text-primary-400">
          <Icon className="h-3.5 w-3.5" /> {meta.label}
        </span>
        <span className="min-w-0 truncate text-sm font-medium text-gray-900 dark:text-gray-100">{target}</span>
        {detailLink && (
          <Link
            to={detailLink}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
          >
            Open editor <ExternalLink className="h-3 w-3" />
          </Link>
        )}
      </div>
      {proposal.rationale && (
        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{proposal.rationale}</p>
      )}

      <div className="mt-2">
        {proposal.kind === 'settings' ? (
          <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            {settingsRows(proposal.patch || {}, proposal.current).map((r) => (
              <div key={r.path} className="flex items-center gap-2 border-b border-gray-100 px-2.5 py-1.5 text-sm last:border-b-0 dark:border-gray-700/60">
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-gray-600 dark:text-gray-300">{r.path}</span>
                <span className="tabular-nums text-gray-400 line-through dark:text-gray-500">{r.from ?? '—'}</span>
                <span className="text-gray-400">→</span>
                <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">{String(r.to)}</span>
              </div>
            ))}
          </div>
        ) : (
          <DiffBlock
            oldText={(proposal.current as string | null) ?? null}
            newText={proposal.kind === 'prompt' ? (proposal.new_template || '') : (proposal.new_text || '')}
          />
        )}
      </div>

      {proposal.checks && (
        checksFailed ? (
          <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <span className="inline-flex items-center gap-1 font-medium"><ShieldAlert className="h-3.5 w-3.5" /> Would fail publish checks:</span>
            <ul className="mt-0.5 list-inside list-disc">
              {proposal.checks.failures.map((f, idx) => <li key={idx}>{f.id}: {f.reason}</li>)}
            </ul>
          </div>
        ) : (
          <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
            <Check className="h-3.5 w-3.5" /> Passes publish checks
          </div>
        )
      )}

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        {cardState.state === 'published' ? (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 dark:text-green-400">
            <CheckCheck className="h-4 w-4" /> Published{cardState.version ? ` (v${cardState.version})` : ''} — live for the agent
          </span>
        ) : proposal.kind === 'settings' ? (
          cardState.state === 'applied' ? (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 dark:text-green-400">
              <CheckCheck className="h-4 w-4" /> Settings applied
            </span>
          ) : (
            <button type="button" onClick={onApply} disabled={busy} className={primaryBtnCls}>
              {cardState.state === 'applying' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Apply settings
            </button>
          )
        ) : (
          <>
            {cardState.state === 'applied' || cardState.state === 'publishing' ? (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-gray-300">
                <Check className="h-4 w-4 text-green-600 dark:text-green-400" /> Draft saved
              </span>
            ) : (
              <button type="button" onClick={onApply} disabled={busy} className={secondaryBtnCls}>
                {cardState.state === 'applying' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Apply to draft
              </button>
            )}
            {(cardState.state === 'applied' || cardState.state === 'publishing') && (
              <button type="button" onClick={onPublish} disabled={busy || checksFailed === true} className={primaryBtnCls}>
                {cardState.state === 'publishing' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4" />}
                Publish
              </button>
            )}
          </>
        )}
        {cardState.state === 'error' && (
          <span className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
            <AlertTriangle className="h-4 w-4" /> {cardState.message}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

const STARTERS = [
  'The agent sounds too stiff — make the tone warmer and more casual.',
  'Reviewers keep shortening the drafts. Tighten the message-length guidance.',
  'Slow down the nudges — wait longer before the first follow-up.',
  'Update this study’s brief: the compensation section is out of date.',
];

let proposalSeq = 0;

export default function AgentTuning() {
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [studies, setStudies] = useState<TargetStudy[]>([]);
  const [prompts, setPrompts] = useState<TargetPrompt[]>([]);
  const [studyId, setStudyId] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [cards, setCards] = useState<Record<string, CardState>>({});
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    let mounted = true;
    api.get<{ prompts: TargetPrompt[]; studies: TargetStudy[] }>('/api/crm/agent/tuner/targets')
      .then((r) => {
        if (!mounted) return;
        setPrompts(r.data?.prompts || []);
        setStudies(r.data?.studies || []);
      })
      .catch((err) => {
        if (!mounted) return;
        if (err?.response?.status === 403) setForbidden(true);
        else console.error('[AgentTuning] targets fetch failed:', err);
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'nearest' });
  }, [messages, sending]);

  const promptNames = useMemo(() => new Map(prompts.map((p) => [p.key, p.name])), [prompts]);
  const studyTitle = useCallback(
    (id: string | undefined) => (id ? (studies.find((s) => s.id === id)?.title || 'Study brief') : 'Study brief'),
    [studies],
  );

  const send = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    const userMsg: ChatMsg = { role: 'user', content };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput('');
    setSending(true);
    try {
      const res = await api.post('/api/crm/agent/tuner/chat', {
        study_id: studyId || null,
        // Assistant turns carry a compact summary of what they proposed —
        // the backend is stateless, and without this the model forgets its
        // own cards ("tweak that draft" would otherwise start from scratch).
        messages: history.map((m) => ({
          role: m.role,
          content: m.role === 'assistant' && m.proposals?.length
            ? `${m.content}\n\n[proposed: ${m.proposals.map(({ proposal: p }) => (p.kind === 'prompt' ? `prompt ${p.key}` : p.kind)).join(', ')}]`
            : m.content,
        })),
      });
      const proposals = (res.data?.proposals || []).map((p: TunerProposal) => ({ id: `p${++proposalSeq}`, proposal: p }));
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: res.data?.reply || '(no reply)',
        proposals,
        notes: res.data?.notes || [],
      }]);
    } catch (err: any) {
      console.error('[AgentTuning] chat failed:', err);
      const detail = err?.response?.data?.detail || err?.response?.data?.error || 'Request failed — try again.';
      setMessages((prev) => [...prev, { role: 'assistant', content: `Something went wrong: ${detail}`, isError: true }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const setCard = (id: string, state: CardState) => setCards((prev) => ({ ...prev, [id]: state }));

  const applyProposal = async (id: string, proposal: TunerProposal) => {
    setCard(id, { state: 'applying' });
    try {
      const res = await api.post('/api/crm/agent/tuner/apply', { proposal });
      if (proposal.kind === 'settings') setCard(id, { state: 'applied' });
      else setCard(id, { state: 'applied', artifactId: res.data?.artifact?.id });
    } catch (err: any) {
      console.error('[AgentTuning] apply failed:', err);
      setCard(id, { state: 'error', message: err?.response?.data?.error || 'Apply failed' });
    }
  };

  const publishProposal = async (id: string, proposal: TunerProposal, cardState: CardState) => {
    const artifactId = 'artifactId' in cardState ? cardState.artifactId : undefined;
    setCard(id, { state: 'publishing', artifactId });
    try {
      if (proposal.kind === 'prompt') {
        const res = await api.post(`/api/crm/prompts/${proposal.key}/publish`, {
          template: proposal.new_template,
          change_note: proposal.rationale || 'Tuning copilot edit',
        });
        setCard(id, { state: 'published', version: res.data?.version?.version });
      } else if (proposal.kind === 'brief' && artifactId) {
        const res = await api.post(`/api/crm/artifacts/${artifactId}/publish`, {});
        setCard(id, { state: 'published', version: res.data?.artifact?.version });
      }
    } catch (err: any) {
      console.error('[AgentTuning] publish failed:', err);
      setCard(id, { state: 'error', message: err?.response?.data?.error || 'Publish failed', artifactId });
    }
  };

  if (forbidden) {
    return (
      <div className="mt-4 rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
        <Sparkles className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
        <p className="text-lg font-medium text-gray-900 dark:text-gray-100">Tuning is admin-only</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
          Prompt, brief, and settings edits are restricted to CRM administrators.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3">
      {/* Context toolbar — chromeless (filters are page chrome, not content) */}
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="tuner-study" className="text-xs font-medium text-gray-500 dark:text-gray-400">Study in focus</label>
        <select
          id="tuner-study"
          value={studyId}
          onChange={(e) => setStudyId(e.target.value)}
          className={`${fieldCls} max-w-xs`}
        >
          <option value="">None (prompts &amp; settings only)</option>
          {studies.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}{s.brief.published ? '' : s.brief.draft ? ' (draft brief)' : ' (no brief)'}
            </option>
          ))}
        </select>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          Pick a study to let the copilot edit its agent brief.
        </span>
      </div>

      {/* Chat surface */}
      <div className="mt-3 flex flex-col rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="max-h-[calc(100vh-380px)] min-h-[280px] flex-1 overflow-y-auto p-3 sm:p-4" aria-live="polite">
          {messages.length === 0 ? (
            <div className="flex h-full min-h-[240px] flex-col items-center justify-center text-center">
              <Sparkles className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Tell me how the agent should behave differently</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
                I read the agent&apos;s prompts, templates, briefs, settings, and your recent draft edits/rejections — then propose
                exact changes you can review, apply as drafts, and publish.
              </p>
              <div className="mt-4 flex max-w-xl flex-wrap items-center justify-center gap-1.5">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { setInput(s); inputRef.current?.focus(); }}
                    className="inline-flex min-h-[44px] items-center whitespace-normal rounded-full border border-gray-300 px-3 py-1 text-left text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0 sm:py-1.5"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((m, mi) => (
                <div key={mi} className={m.role === 'user' ? 'flex justify-end' : ''}>
                  {m.role === 'user' ? (
                    <div className="max-w-[85%] rounded-xl rounded-br-sm bg-primary-600 px-3 py-2 text-sm text-white">
                      {m.content}
                    </div>
                  ) : (
                    <div className="max-w-[95%]">
                      <div className={`rounded-xl rounded-bl-sm border px-3 py-2 text-sm ${m.isError
                        ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'
                        : 'border-gray-200 bg-gray-50 text-gray-900 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-100'}`}
                      >
                        <MarkdownText text={m.content} />
                      </div>
                      {(m.proposals || []).map(({ id, proposal }) => (
                        <ProposalCard
                          key={id}
                          proposal={proposal}
                          cardState={cards[id] || { state: 'idle' }}
                          promptNames={promptNames}
                          studyTitle={studyTitle}
                          onApply={() => applyProposal(id, proposal)}
                          onPublish={() => publishProposal(id, proposal, cards[id] || { state: 'idle' })}
                        />
                      ))}
                      {(m.notes || []).length > 0 && (
                        <p className="mt-1 text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
                          {(m.notes || []).join(' · ')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" /> Thinking…
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-gray-100 p-2.5 dark:border-gray-700">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              rows={2}
              placeholder="Describe the change — e.g. “stop asking two questions at once” or “the brief’s visit count is wrong, it’s 6 visits”"
              aria-label="Message the tuning copilot"
              className={`${fieldCls} max-h-40 w-full resize-y`}
            />
            <button
              type="button"
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className={primaryBtnCls}
              aria-label="Send"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
              Send
            </button>
          </div>
          <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
            The copilot only writes drafts — you publish. Prompts live in{' '}
            <Link to="/crm/settings/prompts" className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">Settings → AI prompts</Link>;
            briefs on each study&apos;s Agent tab.
          </p>
        </div>
      </div>
    </div>
  );
}
